import React, { useMemo, useState } from 'react';
import ReportPeriodFilter from './ReportPeriodFilter';
import WidgetRenderer from './WidgetRenderer';
import CalcTimeChip from './CalcTimeChip';
import { ReportDataProvider } from './ReportDataContext';
import { getCurrentPersianDate } from '../../../shared/utils/persianDate';
import { cn } from '../../../lib/utils';
import { REPORT_COLS, VIEWER_ROW_PX, VIEWER_GAP } from '../utils/reportLayout';
import { readReportPeriod, writeReportPeriod } from '../utils/reportPeriodSession';

const COLS = REPORT_COLS;
const ROW_PX = VIEWER_ROW_PX;
const GAP = VIEWER_GAP;

/**
 * Map RGL layout item → CSS Grid placement.
 * Uses a 12-column CSS grid (Bootstrap-style cols) — NO absolute positioning,
 * so widgets never slide behind the app sidebar or off-page in RTL.
 */
function placeStyle(item, { fit = false } = {}) {
  const w = Math.max(1, Math.min(Number(item?.w) || COLS, COLS));
  const h = Math.max(1, Number(item?.h) || 4);
  const x = Math.max(0, Math.min(Number(item?.x) || 0, COLS - w));
  const y = Math.max(0, Number(item?.y) || 0);
  return {
    gridColumn: `${x + 1} / span ${w}`,
    gridRow: `${y + 1} / span ${h}`,
    minHeight: fit ? 0 : h * ROW_PX,
    height: fit ? 'auto' : undefined,
    alignSelf: fit ? 'start' : 'stretch',
  };
}

/** Mobile: ignore x/y, stack in reading order (top→bottom, right→left in RTL feel via source order). */
function sortByPosition(widgets, layoutMap) {
  return [...widgets].sort((a, b) => {
    const la = layoutMap[a.id] || { x: 0, y: 0 };
    const lb = layoutMap[b.id] || { x: 0, y: 0 };
    if ((la.y || 0) !== (lb.y || 0)) return (la.y || 0) - (lb.y || 0);
    return (la.x || 0) - (lb.x || 0);
  });
}

export default function ReportViewer({
  report,
  profile,
  compact = false,
  title,
  actions,
}) {
  const [period, setPeriodState] = useState(() => {
    const { year, month, day } = getCurrentPersianDate();
    return readReportPeriod({ year, month, period: 'monthly', day });
  });
  const setPeriod = (next) => {
    setPeriodState(next);
    writeReportPeriod(next);
  };
  const [activeTab, setActiveTab] = useState(report.tabs?.[0]?.id);

  const visibleTabs = useMemo(
    () =>
      (report.tabs || [])
        .filter((t) => t.visible !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [report.tabs],
  );

  const currentTab = visibleTabs.find((t) => t.id === activeTab) || visibleTabs[0];

  const layoutMap = useMemo(() => {
    const map = {};
    (currentTab?.layout || []).forEach((item) => {
      map[item.i] = item;
    });
    return map;
  }, [currentTab?.layout]);

  const widgets = useMemo(
    () => sortByPosition(currentTab?.widgets || [], layoutMap),
    [currentTab?.widgets, layoutMap],
  );

  return (
    <ReportDataProvider report={report} period={period}>
    <div className="rtl w-full min-w-0 overflow-x-hidden">
      <div className="border-b border-indigo-200 bg-[#eef2ff]">
        <div className="flex w-full flex-wrap items-center gap-2 px-4 py-2 sm:px-5">
          {title ? (
            <h1 className="shrink-0 text-base font-semibold text-slate-800">{title}</h1>
          ) : null}
          {actions ? (
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">{actions}</div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 border-t border-indigo-100 px-4 py-1 sm:px-5">
          <CalcTimeChip />
          <ReportPeriodFilter value={period} onChange={setPeriod} />
        </div>
      </div>

      <div className="space-y-3 px-4 py-3 sm:px-5">
      {visibleTabs.length > 1 && (
        <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors shrink-0',
                (activeTab || visibleTabs[0]?.id) === tab.id
                  ? 'border-indigo-500 text-indigo-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {currentTab && (
        <>
          {/*
            Single widget tree — one WidgetRenderer per widget.
            Mobile: flex column (grid placement styles are ignored).
            Desktop (sm+): 12-col CSS grid; dir=ltr keeps designer x columns (col 0 = left).
            Each cell sets dir=rtl for content.
          */}
          <div
            dir="ltr"
            className="flex w-full min-w-0 flex-col sm:grid"
            style={{
              gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))`,
              gridAutoRows: `minmax(${ROW_PX}px, auto)`,
              gap: GAP,
            }}
          >
            {widgets.map((widget) => {
              const lay = layoutMap[widget.id] || { x: 0, y: 0, w: COLS, h: 4 };
              const fit = widget.appearance?.fitContent === true;
              return (
                <div
                  key={widget.id}
                  className={cn(
                    'w-full min-w-0',
                    fit ? 'h-auto overflow-visible' : 'h-auto overflow-hidden sm:h-full',
                  )}
                  style={placeStyle(lay, { fit })}
                  dir="rtl"
                >
                  <WidgetRenderer widget={widget} period={period} profile={profile} />
                </div>
              );
            })}
          </div>

          {widgets.length === 0 && (
            <div className="py-16 text-center text-sm text-slate-400">
              ویجتی در این تب تعریف نشده است
            </div>
          )}
        </>
      )}
      </div>
    </div>
    </ReportDataProvider>
  );
}
